import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Price } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { messageTresorerie } from '../../api/tresorerie.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import styles from './Tresorerie.module.css';

export function TresorerieCadre({
  titre,
  reglages = false,
  children,
}: {
  titre: string;
  reglages?: boolean;
  children: ReactNode;
}) {
  const { galerie, galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  if (
    !galerieAModule(galerie, 'tresorerie') ||
    !galerieALaction(galerie, reglages ? 'treasury:settings' : 'treasury:read')
  )
    return <NotFoundPage />;
  return (
    <div className={styles.page}>
      <h1>{t(`regie:tresorerie.${titre}`)}</h1>
      <nav className={styles.nav} aria-label={t('regie:tresorerie.titre')}>
        {['', 'lignes', 'ecarts', 'depenses', 'prevision'].map((p) => (
          <Link key={p} to={`/${galerieSlug}/tresorerie${p ? `/${p}` : ''}`}>
            {t(`regie:tresorerie.${p || 'titre'}`)}
          </Link>
        ))}
        {galerieALaction(galerie, 'treasury:write') && (
          <Link to={`/${galerieSlug}/tresorerie/import`}>{t('regie:tresorerie.importer')}</Link>
        )}
        {galerieALaction(galerie, 'treasury:settings') && (
          <Link to={`/${galerieSlug}/reglages/tresorerie`}>{t('regie:tresorerie.reglages')}</Link>
        )}
      </nav>
      {children}
    </div>
  );
}
export function Montant({ cents }: { cents: number }) {
  const { i18n } = useTranslation();
  return <Price cents={cents} lang={i18n.language === 'en' ? 'en' : 'fr'} currency="EUR" />;
}
/** Le jour civil à Paris, au format AAAA-MM-JJ des champs de date. */
export function dateParis(date: string): string {
  return new Intl.DateTimeFormat('fr-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(date));
}
/** Une date affichée dans la langue de la régie, au jour de Paris. */
export function DateJour({ date }: { date: string }) {
  const { i18n } = useTranslation();
  return <>{formatDate(new Date(date), i18n.language === 'en' ? 'en' : 'fr', 'court')}</>;
}
export const aujourdhui = () => dateParis(new Date().toISOString());
export function EtatRequete({
  requete,
}: {
  requete: { isPending: boolean; isError: boolean; error: unknown };
}) {
  const { t } = useTranslation('commun');
  return (
    <>
      {requete.isPending && <p role="status">{t('chargement')}</p>}
      {requete.isError && <p role="alert">{messageTresorerie(requete.error)}</p>}
    </>
  );
}
